import React from 'react';
import clsx from 'clsx';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from './Button';

/**
 * What a component shows when it could not get its data.
 *
 * The rule it exists to enforce: this goes **instead of** the content, never on
 * top of it. A red banner above a grid of zeros is worse than no data at all,
 * because a `0` in "Total Deliveries" reads exactly like "there were no
 * shipments" — the screen keeps looking authoritative while being wrong.
 *
 * The only case for `inline` is a component that still has valid data from
 * before: there the old numbers stay and the strip says they are stale.
 *
 * Before this, the same red banner was copied by hand into eight pages and
 * three more had their own "Error al cargar" block — none of which offered to
 * retry, only to go back.
 *
 * @param {object} props
 * @param {React.ComponentType} [props.icon=AlertCircle]
 * @param {string} props.title
 * @param {string} [props.description] - Usually the sentence from `extractApiError`.
 * @param {() => void} [props.onRetry] - Re-runs the request of whoever mounted
 *   this. Nothing retries on its own anywhere in the app.
 * @param {string} [props.retryLabel='Reintentar']
 * @param {boolean} [props.isRetrying=false]
 * @param {'block'|'compact'|'inline'} [props.variant='block'] - `block` for a
 *   component that owns the screen, `compact` inside a card (several can fail at
 *   once and they should not shout), `inline` for a strip over stale content.
 * @param {React.ReactNode} [props.children] - Extra actions, e.g. "Volver".
 */
export const ErrorState = ({
  icon: Icon = AlertCircle,
  title,
  description,
  onRetry,
  retryLabel = 'Reintentar',
  isRetrying = false,
  variant = 'block',
  className = '',
  children,
}) => {
  if (variant === 'inline') {
    return (
      <div
        className={clsx(
          'flex flex-wrap items-center gap-3 bg-red-50 dark:bg-red-900/20 text-danger',
          'px-4 py-3 rounded-xl border border-red-200 dark:border-red-900',
          className
        )}
      >
        <Icon size={16} className="flex-shrink-0" />
        <span className="text-sm font-medium">{title}</span>
        {description && (
          <span className="text-sm text-danger/80 min-w-0 break-words">{description}</span>
        )}
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            className="ml-auto text-sm font-semibold underline underline-offset-2 hover:opacity-80 disabled:opacity-50"
          >
            {isRetrying ? 'Reintentando...' : retryLabel}
          </button>
        )}
        {children}
      </div>
    );
  }

  const isCompact = variant === 'compact';

  return (
    <div
      className={clsx(
        'flex flex-col items-center justify-center text-center',
        isCompact ? 'py-8 px-4' : 'py-12 px-6',
        className
      )}
    >
      <div
        className={clsx(
          'rounded-full bg-red-50 dark:bg-red-900/20 text-danger flex items-center justify-center',
          isCompact ? 'w-10 h-10 mb-3' : 'w-16 h-16 mb-4'
        )}
      >
        <Icon size={isCompact ? 18 : 24} />
      </div>

      <h3
        className={clsx(
          'font-bold text-ninja-dark dark:text-white',
          isCompact ? 'text-sm mb-1' : 'text-lg mb-2'
        )}
      >
        {title}
      </h3>

      {description && (
        <p
          className={clsx(
            'text-text-secondary break-words',
            isCompact ? 'text-xs max-w-xs' : 'text-sm max-w-sm'
          )}
        >
          {description}
        </p>
      )}

      {(onRetry || children) && (
        <div className={clsx('flex flex-wrap items-center justify-center gap-2', isCompact ? 'mt-3' : 'mt-5')}>
          {onRetry && (
            <Button
              variant="primary"
              size={isCompact ? 'sm' : 'md'}
              icon={RefreshCw}
              loading={isRetrying}
              loadingLabel="Reintentando..."
              onClick={onRetry}
            >
              {retryLabel}
            </Button>
          )}
          {children}
        </div>
      )}
    </div>
  );
};
